import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { db } from '../firebase'; // Ensure your firebase.js is correctly configured
import { collection, addDoc, Timestamp } from 'firebase/firestore';

const CustomizeARCHITECTProjectForm = () => {
  const PCimage ="https://vizaura.s3.eu-north-1.amazonaws.com/Components/customise.jpg"
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [background, setBackground] = useState(PCimage);

  // Define the WhatsApp number
  const WHATSAPP_NUMBER = '919016663115'; // Use the number without '+' for the wa.me link

  useEffect(() => {
    // This effect ensures the background image is set initially and on resize.
    // Given PCimage is the only background, the resize logic here might be redundant
    // unless you plan to add a mobile-specific image later.
    const handleResize = () => {
      // In this specific code, the background is always PCimage,
      // so the resize logic essentially just re-sets it to the same image.
      // If you had a MOBILEimage like in your previous example, you'd put the conditional logic here.
      setBackground(PCimage);
    };

    handleResize(); // Set initial background
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // 1. Store inquiry details in Firebase Firestore
      await addDoc(collection(db, 'custom_projects'), {
        name,
        contact,
        message,
        createdAt: Timestamp.now()
      });

      // 2. Prepare the WhatsApp message and open WhatsApp
      const whatsappMessage = `\nHello, I'm ${name}.\n My contact number is ${contact}.\n I'm an architect/interior designer interested in custom artwork.\n Here's my vision: ${message}\n`;
      const encodedMessage = encodeURIComponent(whatsappMessage);
      const whatsappURL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

      // Open WhatsApp in a new tab/window
      window.open(whatsappURL, '_blank');

      // Set success message and clear form fields after both operations are initiated
      setSuccessMsg("Inquiry submitted successfully! Redirecting to WhatsApp...");
      setName('');
      setContact('');
      setMessage('');

    } catch (error) {
      console.error("Error submitting inquiry:", error);
      setSuccessMsg("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="py-24 px-6 relative bg-cover bg-center font-serif"
      style={{
        backgroundImage: `url(${background})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0"></div> {/* Added a subtle overlay for better text readability */}

      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-16 items-center relative z-10">
        <motion.div
          className="md:col-span-2 space-y-6 text-center md:text-left text-white"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-5xl font-serif leading-tight">
            ARE YOU AN ARCHITECT <br/>OR INTERIOR DESIGNER?
          </h2>
          <h2 className="text-xl md:text-2xl mt-7 leading-relaxed">
            We love collaborating with architects and interior designers to create custom artworks and graphics specially designed for your project and vision.
          </h2>
          <h2 className="text-lg md:text-xl mt-7 leading-relaxed">
            Contact us for more details.
          </h2>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          className="md:col-span-1 space-y-6 bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-2xl"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          <div>
            <label htmlFor="name" className="block text-sm font-serif font-semibold text-gray-200 mb-2">Full Name</label>
            <input
              id="name"
              type="text"
              placeholder="e.g., John Doe"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-5 py-4 bg-black/20 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 placeholder-gray-400"
            />
          </div>
          <div>
            <label htmlFor="contact" className="block text-sm font-serif font-semibold text-gray-200 mb-2">Contact Number</label>
            <input
              id="contact"
              type="tel"
              placeholder="e.g., +91 98765 43210"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              required
              className="w-full px-5 py-4 bg-black/20 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 placeholder-gray-400"
            />
          </div>
          <div>
            <label htmlFor="message" className="block text-sm font-serif font-semibold text-gray-200 mb-2">Your Vision</label>
            <textarea
              id="message"
              rows="4"
              placeholder="Tell us about your project or inquiry..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              className="w-full px-5 py-4 bg-black/20 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 placeholder-gray-400"
            ></textarea>
          </div>

          {successMsg && (
            <p className="text-green-300 text-sm font-serif">{successMsg}</p>
          )}

          <motion.button
            type="submit"
            className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-4 rounded-lg transition-all duration-300 shadow-lg"
            whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(245, 158, 11, 0.6)' }}
            whileTap={{ scale: 0.98 }}
            disabled={loading}
          >
            {loading ? "Sending..." : "Submit Inquiry"}
          </motion.button>
        </motion.form>
      </div>
    </div>
  );
};

export default CustomizeARCHITECTProjectForm;